import React, { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useLanguage } from "@shared/contexts/LanguageContext";
import { QrCode, Camera, Menu, Hash } from "lucide-react";
import PreferencesPanel from "@visitor/components/PreferencesPanel";
import BarcodeScanner from "react-qr-barcode-scanner";

const DX_LOGO_URL =
  "https://cdn.art-mate.net/uploads/artmate/202502/thumbnail/20250228_160227_1YqJgAgCUS_p_300_0.png";

const NewQrInstructions = () => {
  const { fontSize, t, setAccessMethod } = useLanguage();
  const navigate = useNavigate();
  const [preferencesOpen, setPreferencesOpen] = useState(false);
  const [scannerOpen, setScannerOpen] = useState(false);
  const [scanError, setScanError] = useState("");

  // Font size classes based on global preference
  const getFontSizeClass = () => {
    if (fontSize === "small") return "text-sm";
    if (fontSize === "large") return "text-xl";
    return "text-base";
  };

  const parseInternalPath = useCallback((value) => {
    if (!value) return null;

    const raw = String(value).trim();
    let pathname = null;
    let hostname = null;

    try {
      // Full URL QR codes (most common)
      const url = new URL(raw);
      pathname = url.pathname;
      hostname = url.hostname;
      console.log("Parsed URL:", url);
      console.log("Hostname:", hostname);
      console.log("pathname:", pathname);
    } catch {
      // Path-only QR codes
      if (raw.startsWith("/")) pathname = raw;
      else if (raw.startsWith("exhibit/") || raw.startsWith("zone/"))
        pathname = `/${raw}`;
    }

    if (!pathname || !hostname || hostname !== window.location.hostname)
      return null;
    const match = pathname.match(/^\/(exhibit|zone)\/([a-z0-9A-Z]+)(\/)?$/);
    if (!match) return null;
    return `/${match[1]}/${match[2]}`;
  }, []);

  const scannerHint = useMemo(() => {
    return t({
      en: "Or scan directly in this page",
      tc: "或直接在本頁掃描",
      sc: "或直接在本页扫描",
    });
  }, [t]);

  const handleScanResult = useCallback(
    (value) => {
      console.log("Scanned value:", value);
      const internalPath = parseInternalPath(value);
      console.log("Parsed internal path:", internalPath);
      if (!internalPath) {
        setScanError(
          t({
            en: "QR code not recognized. Please scan a museum QR code.",
            tc: "無法識別二維碼，請掃描博物館提供的二維碼。",
            sc: "无法识别二维码，请扫描博物馆提供的二维码。",
          }),
        );
        return;
      }

      setScanError("");
      setAccessMethod("qr");
      setScannerOpen(false);
      navigate(internalPath);
    },
    [navigate, parseInternalPath, setAccessMethod, t],
  );

  const handleScanError = useCallback(
    (err) => {
      // Only show a short message; detailed errors vary by browser.
      const msg =
        err?.name === "NotAllowedError"
          ? t({
              en: "Camera permission denied.",
              tc: "相機權限被拒絕。",
              sc: "相机权限被拒绝。",
            })
          : t({
              en: "Unable to access camera on this browser.",
              tc: "此瀏覽器無法使用相機。",
              sc: "此浏览器无法使用相机。",
            });
      setScanError(msg);
    },
    [t],
  );

  return (
    <div
      className="min-h-screen flex flex-col bg-black"
      data-testid="qr-instructions-page"
    >
      {/* Header */}
      <div className="sticky top-0 bg-black/90 backdrop-blur border-b border-white/10 z-10">
        <div className="max-w-4xl mx-auto px-4 flex items-center justify-between relative">
          <img
            src={DX_LOGO_URL}
            alt="DX Logo"
            className="w-28 object-contain"
          />
          <button
            onClick={() => setPreferencesOpen(true)}
            className="text-white hover:bg-white/10 rounded-lg p-2"
            data-testid="open-preferences"
          >
            <Menu className="w-8 h-8" strokeWidth={1.5} />
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 flex items-center justify-center px-4 py-8">
        <div className="self-start">
          <h1 className="absolute left-1/2 -translate-x-1/2 text-2xl font-heading font-bold text-white">
            {t({ en: "Scan QR Code", tc: "掃描二維碼", sc: "扫描二维码" })}
          </h1>
        </div>
        <div className="w-full max-w-md space-y-8 mt-10">
          {/* Icon */}
          <div className="flex flex-col items-center space-y-6">
            <div className="text-center space-y-4">
              <p
                className={`text-white/70 leading-relaxed ${getFontSizeClass()}`}
              >
                {t({
                  en: "Point your phone camera at the QR code displayed near any exhibit to view its details",
                  tc: "將手機相機鏡頭對準展品旁的二維碼即可查看展品詳情",
                  sc: "将手机相机镜头对准展品旁的二维码即可查看展品详情",
                })}
              </p>
            </div>
          </div>

          {/* In-page QR Scanner */}
          <div className="space-y-4 bg-white/5 p-6 rounded-xl border border-white/10">
            <BarcodeScanner
              width={500}
              height={500}
              onUpdate={(err, result) => {
                if (err) {
                  console.error(err);
                  return;
                }
                console.log("Scanner update:", { err, result });
                if (result) {
                  const scannedText = result.text;
                  handleScanResult(scannedText);
                }
              }}
            />
            {!!scanError && (
              <p className={`text-sm text-red-300 ${getFontSizeClass()}`}>
                {scanError}
              </p>
            )}
          </div>

          {/* Enter Code Link with Icon */}
          <div className="text-center space-y-3">
            <p className={`text-white/50 ${getFontSizeClass()}`}>
              {t({
                en: "Having trouble scanning?",
                tc: "掃描遇到問題？",
                sc: "扫描遇到问题？",
              })}
            </p>
            {/* Instructions */}
            <div className="space-y-4 bg-white/5 p-6 rounded-xl border border-white/10">
              <h3 className={`font-semibold text-white ${getFontSizeClass()}`}>
                {t({
                  en: "Use your phone camera app:",
                  tc: "使用手機相機應用程式：",
                  sc: "使用手机相机应用程序：",
                })}
              </h3>
              <ol className={`space-y-3 text-white/70 ${getFontSizeClass()}`}>
                <li className="flex gap-3">
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-white text-black flex items-center justify-center text-sm font-bold">
                    1
                  </span>
                  <span>
                    {t({
                      en: "Open your phone camera app",
                      tc: "打開手機相機應用程式",
                      sc: "打开手机相机应用程序",
                    })}
                  </span>
                </li>
                <li className="flex gap-3">
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-white text-black flex items-center justify-center text-sm font-bold">
                    2
                  </span>
                  <span>
                    {t({
                      en: "Point it at the QR code near the exhibit",
                      tc: "將鏡頭對準展品旁的二維碼",
                      sc: "将镜头对准展品旁的二维码",
                    })}
                  </span>
                </li>
                <li className="flex gap-3">
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-white text-black flex items-center justify-center text-sm font-bold">
                    3
                  </span>
                  <span>
                    {t({
                      en: "Tap the notification to open the exhibit page",
                      tc: "點擊通知以開啟展品頁面",
                      sc: "点击通知以打开展品页面",
                    })}
                  </span>
                </li>
              </ol>
            </div>
            {/* <p className={`text-white/50 ${getFontSizeClass()}`}>
              {t({
                en: 'Or',
                tc: '或者',
                sc: '或者'
              })}
            </p>
            <button
              onClick={() => {
                setAccessMethod('code');
                navigate('/code-entry');
              }}
              className="flex items-center justify-center gap-3 mx-auto px-6 py-3 rounded-xl bg-white/5 border border-white/20 hover:bg-white/10 transition-all"
              data-testid="use-code-instead"
            >
              <div className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center">
                <Hash className="w-7 h-7 text-white" />
              </div>
              <span className={`text-white font-medium ${getFontSizeClass()}`}>
                {t({
                  en: 'Enter code instead',
                  tc: '改用輸入代碼',
                  sc: '改用输入代码'
                })}
              </span>
            </button> */}
          </div>
        </div>
      </div>

      {/* Preferences Panel */}
      <PreferencesPanel
        isOpen={preferencesOpen}
        onClose={() => setPreferencesOpen(false)}
      />
    </div>
  );
};

export default NewQrInstructions;
